import Box from '@mui/material/Box';
import {keyframes} from '@mui/material/styles';
import {useMemo} from 'react';

interface Flake {
  left: number;
  /** Resting position when the animation is off (reduced motion) */
  top: number;
  size: number;
  duration: number;
  delay: number;
  drift: number;
  opacity: number;
}

// `top` is animated (instead of a transform) so that the distance is relative to the container's height.
const fall = keyframes`
  0% { top: -6%; transform: translate3d(0, 0, 0); }
  100% { top: 106%; transform: translate3d(var(--drift), 0, 0); }
`;

/** Deterministic pseudo random numbers, so that the flakes do not jump around on re-renders. */
function random(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

interface Props {
  /** Number of flakes, default 24 */
  count?: number;
}

/**
 * Gentle snowfall inside the nearest positioned ancestor (which should have `overflow: hidden`). Purely
 * decorative: it ignores pointer events and uses only CSS animations; if the user prefers reduced motion the
 * flakes rest in place instead of falling.
 */
export default function Snowfall({count = 24}: Props) {
  const flakes = useMemo<Flake[]>(() => {
    const next = random(42);
    return Array.from({length: count}, () => ({
      left: next() * 100,
      top: next() * 100,
      size: 3 + next() * 5,
      duration: 7 + next() * 9,
      delay: -next() * 16,
      drift: (next() - 0.5) * 60,
      opacity: 0.4 + next() * 0.5,
    }));
  }, [count]);

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {flakes.map((flake, index) => (
        <Box
          key={index}
          sx={{
            position: 'absolute',
            left: `${flake.left}%`,
            top: `${flake.top}%`,
            width: flake.size,
            height: flake.size,
            borderRadius: '50%',
            backgroundColor: '#ffffff',
            opacity: flake.opacity,
            boxShadow: '0 0 6px rgba(255, 255, 255, 0.9)',
            '--drift': `${flake.drift}px`,
            animation: `${fall} ${flake.duration}s linear ${flake.delay}s infinite`,
            '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
          }}
        />
      ))}
    </Box>
  );
}
